import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import type { FixtureData, SnowflakeKpis, SnowflakeValidationStatus } from './types';

export function formatExecutionTime(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

export function formatTimeDelta(avMs: number, legacyMs: number): { text: string; color: string } {
  const deltaMs = avMs - legacyMs;
  const deltaSec = Math.abs(deltaMs) / 1000;
  if (deltaMs > 0) {
    return { text: `(+${deltaSec.toFixed(2)}s)`, color: 'red.800' };
  } else if (deltaMs < 0) {
    return { text: `(-${deltaSec.toFixed(2)}s)`, color: 'green.800' };
  }
  return { text: '(+0.00s)', color: 'grey.600' };
}

export function computeSnowflakeKpis(
  fixtures: FixtureData[],
  fallback: SnowflakeKpis
): SnowflakeKpis {
  const withValidation = fixtures.filter((f) => f.snowflakeValidation);
  if (withValidation.length === 0) return fallback;

  const skipped = withValidation.filter((f) => f.snowflakeValidation!.status === 'skipped');
  const nonSkipped = withValidation.filter((f) => f.snowflakeValidation!.status !== 'skipped');

  const skipReasons: Record<string, number> = {};
  for (const f of skipped) {
    const reason = f.snowflakeValidation!.skipReason || 'unknown';
    skipReasons[reason] = (skipReasons[reason] || 0) + 1;
  }

  const avIrSuccess = nonSkipped.filter(
    (f) => f.snowflakeValidation!.avIr && !f.snowflakeValidation!.avIr.error
  );
  const avIrError = nonSkipped.filter(
    (f) => f.snowflakeValidation!.avIr && f.snowflakeValidation!.avIr.error
  );
  const avIrEmpty = avIrSuccess.filter((f) => f.snowflakeValidation!.avIr!.rowCount === 0);
  const avIrWithRows = avIrSuccess.length - avIrEmpty.length;

  const compared = nonSkipped.filter((f) => f.snowflakeValidation!.comparison !== null);
  const matchCount = compared.filter(
    (f) => f.snowflakeValidation!.comparison!.personIdSetMatch
  ).length;
  const legacyError = nonSkipped.filter(
    (f) =>
      f.snowflakeValidation!.legacy?.error &&
      f.snowflakeValidation!.avIr &&
      !f.snowflakeValidation!.avIr.error
  ).length;

  const reviewedNonSkipped = nonSkipped.filter((f) => f.reviewed);
  const reviewedCompared = compared.filter((f) => f.reviewed);

  return {
    totalFixtures: withValidation.length,
    reviewedFixtures: withValidation.filter((f) => f.reviewed).length,
    skippedCount: skipped.length,
    skippedReasons: skipReasons,
    avIr: {
      executedCount: nonSkipped.length,
      successCount: avIrSuccess.length,
      errorCount: avIrError.length,
      emptyCount: avIrEmpty.length,
      withRowsCount: avIrWithRows,
    },
    reviewedExecutedCount: reviewedNonSkipped.length,
    comparison: {
      comparedCount: compared.length,
      matchCount,
      mismatchCount: compared.length - matchCount,
      legacyErrorCount: legacyError,
    },
    reviewedComparedCount: reviewedCompared.length,
    dataset: fallback.dataset,
    lastRunAt: fallback.lastRunAt,
  };
}

export const VALIDATION_STATUS_CHIP: Record<
  SnowflakeValidationStatus,
  { color: string; bgcolor: string; label: string }
> = {
  pass: { color: 'green.800', bgcolor: 'green.50', label: 'Pass' },
  fail: { color: 'red.800', bgcolor: '#fef2f2', label: 'Fail' }, // TODO: no VDS token for bgcolor — needs designer input (Barry Richardson)
  error: { color: 'orange.900', bgcolor: 'orange.50', label: 'Error' },
  warn_empty: { color: '#f57f17', bgcolor: 'yellow.50', label: 'Warn (Empty)' }, // TODO: no VDS token for color — needs designer input (Barry Richardson)
  skipped: { color: 'bluegrey.600', bgcolor: 'bluegrey.50', label: 'Skipped' },
  pending: { color: 'grey.600', bgcolor: 'grey.50', label: 'Pending' },
};

const SKIP_REASON_LABELS: Record<string, string> = {
  no_av_ir_sql: 'No AV IR SQL',
  placeholder_concept_ids: 'Placeholder Concept IDs',
  not_expressible: 'Not Expressible',
};

export function NotesLink({ title, notes }: { title: string; notes: string[] }) {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  if (notes.length === 0) return null;

  return (
    <>
      <Link
        component="button"
        variant="body2"
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {notes.length === 1 ? '1 note' : `${notes.length} notes`}
      </Link>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {notes.map((note, i) => (
              <Typography key={i} component="li" variant="body1" sx={{ mb: 0.5 }}>
                {note}
              </Typography>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SummaryCard({
  label,
  count,
  total,
  color,
  bgcolor,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
  bgcolor: string;
}) {
  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        p: 2,
        borderRadius: `${borderRadius.xl}px`,
        bgcolor,
        border: '1px solid',
        borderColor: color,
      }}
    >
      <Typography variant="h3" fontWeight={700} color={color}>
        {count}
      </Typography>
      <Typography variant="body1" color={color} fontWeight={600}>
        {label} ({percent}%)
      </Typography>
    </Box>
  );
}

function StatusChip({ status }: { status: SnowflakeValidationStatus }) {
  const chip = VALIDATION_STATUS_CHIP[status];
  return (
    <Chip
      label={chip.label}
      size="small"
      sx={{
        bgcolor: chip.bgcolor,
        color: chip.color,
        fontWeight: 600,
        fontSize: '1rem',
        border: '1px solid',
        borderColor: chip.color,
      }}
    />
  );
}

interface ValidationDashboardProps {
  fixtures: FixtureData[];
  kpis: SnowflakeKpis;
  onNavigateToFixture: (fixtureId: string) => void;
}

export function ValidationDashboard({
  fixtures,
  kpis,
  onNavigateToFixture,
}: ValidationDashboardProps) {
  const failuresAndErrors = useMemo(
    () =>
      fixtures
        .filter((f) => {
          const s = f.snowflakeValidation?.status;
          return s === 'fail' || s === 'error';
        })
        .sort((a, b) => {
          const order: Record<string, number> = { fail: 0, error: 1 };
          const aOrder = order[a.snowflakeValidation?.status ?? ''] ?? 2;
          const bOrder = order[b.snowflakeValidation?.status ?? ''] ?? 2;
          return aOrder !== bOrder ? aOrder - bOrder : a.id.localeCompare(b.id);
        }),
    [fixtures]
  );

  const validatedFixtures = useMemo(
    () =>
      fixtures
        .filter((f) => {
          const s = f.snowflakeValidation?.status;
          return s && s !== 'pending';
        })
        .sort((a, b) => {
          const order: Record<string, number> = {
            fail: 0,
            error: 1,
            warn_empty: 2,
            pass: 3,
            skipped: 4,
          };
          const aOrder = order[a.snowflakeValidation?.status ?? ''] ?? 5;
          const bOrder = order[b.snowflakeValidation?.status ?? ''] ?? 5;
          return aOrder !== bOrder ? aOrder - bOrder : a.id.localeCompare(b.id);
        }),
    [fixtures]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Dataset label */}
      {kpis.dataset && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" color="text.secondary">
            Dataset:
          </Typography>
          <Typography variant="body1" fontFamily="monospace" fontWeight={600}>
            {kpis.dataset}
          </Typography>
          {kpis.lastRunAt && (
            <Typography variant="body2" color="text.disabled">
              — {new Date(kpis.lastRunAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}

      {/* AV IR Execution cards */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          AV IR Execution
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <SummaryCard
            label="Success"
            count={kpis.avIr.successCount}
            total={kpis.avIr.executedCount}
            color="green.800"
            bgcolor="green.50"
          />
          <SummaryCard
            label="With Rows"
            count={kpis.avIr.withRowsCount}
            total={kpis.avIr.executedCount}
            color="primary.main"
            bgcolor="lightblue.50"
          />
          <SummaryCard
            label="Empty"
            count={kpis.avIr.emptyCount}
            total={kpis.avIr.executedCount}
            color="#f57f17" // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            bgcolor="yellow.50"
          />
          <SummaryCard
            label="Errors"
            count={kpis.avIr.errorCount}
            total={kpis.avIr.executedCount}
            color="orange.900"
            bgcolor="orange.50"
          />
        </Box>
      </Box>

      {/* Legacy Comparison cards */}
      {kpis.comparison.comparedCount > 0 && (
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Legacy Comparison
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <SummaryCard
              label="Match"
              count={kpis.comparison.matchCount}
              total={kpis.comparison.comparedCount}
              color="green.800"
              bgcolor="green.50"
            />
            <SummaryCard
              label="Mismatch"
              count={kpis.comparison.mismatchCount}
              total={kpis.comparison.comparedCount}
              color="red.800"
              bgcolor="#fef2f2" // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            />
            <SummaryCard
              label="Legacy Errors"
              count={kpis.comparison.legacyErrorCount}
              total={kpis.comparison.comparedCount}
              color="orange.900"
              bgcolor="orange.50"
            />
          </Box>
        </Box>
      )}

      {/* Skip reasons */}
      {Object.keys(kpis.skippedReasons).length > 0 && (
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Skip Reasons ({kpis.skippedCount} skipped)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {Object.entries(kpis.skippedReasons).map(([reason, count]) => (
              <Box key={reason} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ minWidth: 240 }}>
                  {SKIP_REASON_LABELS[reason] || reason}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {count}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Failures & Errors */}
      {failuresAndErrors.length > 0 && (
        <Accordion
          defaultExpanded
          disableGutters
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                Failures & Errors ({failuresAndErrors.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  '.Mui-expanded > .MuiAccordionSummary-content &.expand-label': {
                    display: 'none',
                  },
                  '.Mui-expanded > .MuiAccordionSummary-content &.collapse-label': {
                    display: 'inline',
                  },
                }}
                className="expand-label"
              >
                expand
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'none',
                  '.Mui-expanded > .MuiAccordionSummary-content &': { display: 'inline' },
                }}
                className="collapse-label"
              >
                collapse
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {failuresAndErrors.map((f) => {
                const v = f.snowflakeValidation;
                if (!v) return null;
                return (
                  <Box
                    key={f.id}
                    sx={{
                      p: 1.5,
                      borderRadius: `${borderRadius.md}px`,
                      border: '1px solid',
                      borderColor: v.status === 'fail' ? '#fecaca' : '#fed7aa', // TODO: no VDS token for these values — needs designer input (Barry Richardson)
                      bgcolor: v.status === 'fail' ? '#fef2f2' : 'orange.50', // TODO: no VDS token for #fef2f2 — needs designer input (Barry Richardson)
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="body1"
                        fontFamily="monospace"
                        fontWeight={700}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => onNavigateToFixture(f.id)}
                      >
                        {f.id}
                      </Typography>
                      <StatusChip status={v.status} />
                      {v.avIr && (
                        <Typography variant="body2" color="text.secondary">
                          AV: {v.avIr.rowCount} rows ({formatExecutionTime(v.avIr.executionTimeMs)})
                        </Typography>
                      )}
                      {v.legacy && (
                        <Typography variant="body2" color="text.secondary">
                          Legacy: {v.legacy.rowCount} rows (
                          {formatExecutionTime(v.legacy.executionTimeMs)})
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body1" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                      {f.intent}
                    </Typography>
                    {v.failureDetail?.message && (
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: '#1e293b', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          borderRadius: `${borderRadius.md}px`,
                          fontFamily: 'monospace',
                          fontSize: '1rem',
                          color: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {v.failureDetail.message}
                      </Box>
                    )}
                    {v.sqlModifications && v.sqlModifications.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <NotesLink
                          title={`${f.id} — SQL Modifications`}
                          notes={v.sqlModifications}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* All validated fixtures table */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          All Validated Fixtures ({validatedFixtures.length})
        </Typography>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              textAlign: 'left',
              px: 1.5,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              fontSize: '1rem',
            },
            '& th': {
              fontWeight: 700,
              bgcolor: 'grey.50',
            },
          }}
        >
          <thead>
            <tr>
              <th>ID</th>
              <th>Intent</th>
              <th>Status</th>
              <th>AV Rows</th>
              <th>Legacy Rows</th>
              <th>Match</th>
              <th>Time</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {validatedFixtures.map((f) => {
              const v = f.snowflakeValidation;
              if (!v) return null;
              return (
                <tr key={f.id}>
                  <td>
                    <Typography
                      variant="body1"
                      fontFamily="monospace"
                      fontWeight={600}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => onNavigateToFixture(f.id)}
                    >
                      {f.id}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" noWrap sx={{ maxWidth: 300 }}>
                      {f.intent}
                    </Typography>
                  </td>
                  <td>
                    <StatusChip status={v.status} />
                  </td>
                  <td>{v.avIr?.rowCount ?? '-'}</td>
                  <td>{v.legacy?.rowCount ?? '-'}</td>
                  <td>
                    {v.comparison ? (
                      v.comparison.rowCountMatch && v.comparison.personIdSetMatch ? (
                        <Typography
                          component="span"
                          sx={{ color: 'green.800', fontWeight: 700, fontSize: '1.25rem' }}
                        >
                          {'\u2713'}
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: 'red.800', fontWeight: 700, fontSize: '1.25rem' }}
                        >
                          {'\u2717'}
                        </Typography>
                      )
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {v.avIr ? formatExecutionTime(v.avIr.executionTimeMs) : '-'}
                    {v.legacy ? ` / ${formatExecutionTime(v.legacy.executionTimeMs)}` : ''}
                    {v.avIr &&
                      v.legacy &&
                      (() => {
                        const delta = formatTimeDelta(
                          v.avIr.executionTimeMs,
                          v.legacy.executionTimeMs
                        );
                        return (
                          <Typography
                            component="span"
                            sx={{
                              ml: 0.5,
                              color: delta.color,
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {delta.text}
                          </Typography>
                        );
                      })()}
                  </td>
                  <td>
                    {(() => {
                      const notes: string[] = [];
                      if (v.skipReason) {
                        notes.push(SKIP_REASON_LABELS[v.skipReason] || v.skipReason);
                      }
                      if (v.sqlModifications) {
                        notes.push(...v.sqlModifications);
                      }
                      if (v.failureDetail?.message) {
                        notes.push(v.failureDetail.message);
                      }
                      if (notes.length === 0) return '-';
                      return <NotesLink title={f.id} notes={notes} />;
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}
